import { Button, ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import TableActionButtons from '@/components/common/TableActionButtons';
import Table from '@/components/table/Table';
import { BIKE_STATUS_CONFIG, DEFAULT_SELECT_PAGE_SIZES, ROUTES, SUBMIT_MOMENT_DATE_YMD_HM } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { BikeListItem } from '@/types/bike';
import { COMMON_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { CirclePlus, ListFilter, Search } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import NonRentalDateForm from '../../component/NonRentalDateForm';
import FilterForm from './filter';
import Combobox from '@/components/common/Combobox';
import { cn, isDeleted } from '@/utils/utils';
import DeletedLabel from '@/components/general/DeletedLabel';

export default observer(function BikeManagementAdminPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // store
    const { modalStore: { hideAllModals, showModal },
        bikeStore: { bikeList, totalRecord, paging, defaultPaging, keepSearchParams, getAllBike, getListModelNameIncluesDelete, bikeModelListIncluesDeleted, setObservable },
        accountStore: { getAccounts, paging: accountPaging },
        capacitieStore: { getCapacitieList },
        generalStore: { manufacturers, getManufacturers },
        uiStore: { devicesScreen: { desktop, mobile } }

    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        roleUserId: yup.string(),
        bikeOwnerId: yup.string(),
        isShowWeb: yup.boolean(),
        bikeCapacityId: yup.string(),
        bikeModel: yup.string().trim(),
        vehicleStatus: yup.string(),
        locationName: yup.string().trim()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            roleUserId: keepSearchParams?.roleUserId ?? '',
            bikeOwnerId: keepSearchParams?.bikeOwnerId ?? '',
            bikeCapacityId: keepSearchParams?.bikeCapacityId ?? '',
            bikeModel: keepSearchParams?.bikeModel ?? '',
            vehicleStatus: keepSearchParams?.vehicleStatus ?? '',
            locationName: keepSearchParams?.locationName ?? ''
        }
    });

    const bikeModelWatch = form.watch('bikeModel');

    // state
    const [rowSelected, setRowSelected] = useState({});

    // lifecycle

    useEffect(() => {
        if (!keepSearchParams) {
            setObservable('keepSearchParams', { findTime: formatDateTime(new Date(), SUBMIT_MOMENT_DATE_YMD_HM) }, { isMergeObject: false });
        }
        getAllBike(keepSearchParams ? keepSearchParams : { findTime: formatDateTime(new Date(), SUBMIT_MOMENT_DATE_YMD_HM) }, paging);
        getAccounts({ isOwner: true }, { ...accountPaging, pagination: { ...accountPaging.pagination, pageIndex: -2 } });
        getCapacitieList({ type: isPathEk(pathname) ? COMMON_TYPE.EK : COMMON_TYPE.GAS });
        getManufacturers();

        getListModelNameIncluesDelete();
        // return () => {
        //     clean();
        // };
    }, []);

    useEffect(() => {
        mobile && onSearch({ ...keepSearchParams, bikeModel: bikeModelWatch });
    }, [mobile, bikeModelWatch]);

    // function
    const onSearch = (searchParams?: any) => {
        setObservable('keepSearchParams', searchParams, { isMergeObject: false });
        getAllBike(searchParams, defaultPaging);
        hideAllModals();
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getAllBike(keepSearchParams, newPaging);
    };

    const handleClickNonRentalDate = () => {
        showModal({
            id: 'non-rental-date',
            size: '3xl',
            title: t('words_title.non_rental_date'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0',
            content: (
                <NonRentalDateForm
                    isPage={false}
                    bikeIds={Object.keys(rowSelected)
                        .filter(key => rowSelected[key])
                        .map(Number)}
                />
            )
        });
    };

    const handleClickEditBike = (id: number) => {
        navigate(`${ROUTES.bikeEdit.href}/${id}`);
    };

    const handleOpenFilter = () => {
        showModal({
            id: 'filter',
            size: 'lg',
            title: t('words_title.filter'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0 m-0',
            content: <FilterForm form={form} isInModal={true} onSearch={onSearch} />
        });
    };

    // columns
    const columns: ColumnDef<BikeListItem>[] = [
        {
            id: 'actions',
            accessorKey: 'actions',
            header: '',
            size: 50,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => handleClickEditBike(original.id)}
                />
            )
        },
        {
            accessorKey: 'bikeImage',
            header: '',
            size: 120,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[110px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                const imgUrl = original?.vehicleImageRight || original?.vehicleImageLeft || original?.vehicleImageBefore || original?.vehicleImageAfter;
                return (
                    <div className=''>
                        {imgUrl &&
                            <img className='max-w-[70px] h-[50px] object-contain' src={imgUrl} alt={t('field.bike_image')} />}
                    </div>
                );
            }
        },
        {
            accessorKey: 'bikeManufacture',
            header: t('field.manufacturer'),
            size: 100,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[90px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all min-w-[100px] line-clamp-2'>
                    {isPathEk(pathname) ? original.bikeManufacture || '--' : manufacturers.find(item => item.id === Number(original.bikeManufacture))?.name_jp || '--'}
                </p>
            )
        },

        {
            accessorKey: 'bikeModel',
            header: t('field.model_name'),
            size: 250,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[180px]'>
                    {/* <p className='break-all min-w-[120px] line-clamp-2'>
                        {original.bikeModel || '--'}
                    </p> */}
                    <div className='flex flex-wrap justify-start items-center gap-1.5'>
                        {isDeleted(Number(original?.deleteStatus)) && (
                            <div className='inline-block w-fit h-fit'>
                                <DeletedLabel />
                            </div>
                        )}
                        <span className='line-clamp-2 flex-1 min-w-[120px]'>{original.bikeModel ?? ''}</span>
                    </div>
                </div>
            )
        },
        ...isPathEk(pathname) ?
            [] :
            [
                {
                    accessorKey: 'bikeCapacity?.code',
                    header: t('field.engine_size'),
                    size: 150,
                    enableSorting: false,
                    meta: {
                        headerClassName: 'min-w-[80px]',
                        cellClassName: ''
                    },
                    cell: ({ row: { original } }) => (
                        <p className='break-all min-w-[100px]'>{t(`words_title.${original.bikeCapacity?.code}`)}</p>
                    )
                }
            ],
        {
            accessorKey: 'owner.fullName',
            header: t('field.owner'),
            size: 200,
            meta: {
                headerClassName: 'min-w-[80px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all min-w-[120px]'>{original.owner?.fullName || '--'}</p>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'owner.code',
            header: t('field.user_code'),
            size: 200,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all'>{original.owner?.code}</p>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'bikeLicense',
            header: t('field.short_license_plate'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all min-w-[100px] line-clamp-2'>{original.bikeLicense || '--'}</p>
            )
        },
        {
            accessorKey: 'isShowWeb',
            header: t('field.display_on_web'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='min-w-[100px]'>{original.isShowWeb ? t('words_title.yes') : t('words_title.no')}</p>
            )
        },
        {
            accessorKey: 'status',
            header: t('field.status'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                return (
                    // <div className='whitespace-nowrap'>
                    <span className={cn(
                        'whitespace-nowrap',
                        original?.status && `${BIKE_STATUS_CONFIG[original?.status]?.textColor}`
                    )}
                    >
                        {original?.status ? t(`words_title.${BIKE_STATUS_CONFIG[original?.status]?.key}`) : ''}
                    </span>
                    //     {Number(original.deleteStatus) === DELETE_STATUS.IS_DELETE_DISABLE && (
                    //         <DeletedLabel className='ml-1.5' />
                    //     )}
                    // </div>
                );
            }
        }
    ];

    const renderButtonTable = () => {
        return (
            <>
                <ButtonCusWithIcon
                    type='button'
                    variant='outline'
                    size='md'
                    className='w-full h-9.5 md:w-52 md:h-12'
                    onClick={handleClickNonRentalDate}
                    disabled={Object.keys(rowSelected).length < 1}
                    iconId='#icon-calender'
                    widthIcon={18}
                    heightIcon={18}
                >
                    {t('button.non_rental_date')}
                </ButtonCusWithIcon>
                <ButtonCus
                    variant='default'
                    size='md'
                    className='w-full h-9.5 md:w-52 flex gap-2 items-center md:h-12 transition-default'
                    onClick={() => {
                        navigate(ROUTES.bikeCreate.href);
                    }}
                >
                    <CirclePlus />
                    {t('button.add_new')}
                </ButtonCus>
            </>
        );
    };

    return (
        <>
            {desktop ?
                (
                    <div className='layout-card'>
                        <FilterForm form={form} onSearch={onSearch} />
                    </div>
                ) :
                (
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSearch)}>
                            <div className='flex gap-2.5 justify-between relative'>
                                <FormField
                                    control={form.control}
                                    name='bikeModel'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 min-w-0'>
                                            <FormControl>
                                                <div className='relative'>
                                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 z-10 size-5' />
                                                    <Combobox
                                                        className='h-10 pl-10'
                                                        placeholder={t('field.model_name')}
                                                        value={field.value?.toString() ?? ''}
                                                        onChange={(item) => {
                                                            field.onChange(item?.value ?? '');
                                                        }}
                                                        search={true}
                                                        items={
                                                            (Array.isArray(bikeModelListIncluesDeleted) && bikeModelListIncluesDeleted?.length > 0 ?
                                                                [
                                                                    ...bikeModelListIncluesDeleted.map(item => ({
                                                                        label: item.bikeModel,
                                                                        value: item.bikeModel,
                                                                        code: item.bikeModel
                                                                    }))
                                                                ] :
                                                                []
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <div className='min-w-0 w-fit'>
                                    <Button onClick={() => handleOpenFilter()} type='button' variant='outline' size='icon' className='size-10'>
                                        <ListFilter className='size-5' />
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Form>
                )}

            <div className='grid grid-cols-2 md:hidden md:justify-end xsm:gap-4 gap-2 my-4'>
                {renderButtonTable()}
            </div>
            <div className='layout-card md:mt-6'>
                <Table
                    columns={columns}
                    data={bikeList}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                    // enableRowSelection={true}
                    enableRowSelection={({ original }) => !isDeleted(Number(original.deleteStatus))}
                    onRowSelectionChange={setRowSelected}
                    renderActions={(
                        <div className='md:flex hidden justify-end gap-4'>
                            {renderButtonTable()}
                        </div>
                    )}
                />
            </div>
        </>
    );
});
